import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/stores/auth-store';
import { Watermark } from '@/components/layout/Watermark';

/**
 * Where an account lands when the menu has nothing in it for that account.
 *
 * It carries no link back into the console on purpose — every page is refused,
 * so the only move left is to sign out and come back as somebody else, or to
 * ask for the permission by name. The username is printed because whoever the
 * operator asks needs to know which account to widen.
 *
 * It stamps itself, because it is rendered INSTEAD of the shell that stamps
 * every other page — and it is the one screen that prints an account's username
 * on its own, so a screenshot of it is exactly what the stamp exists to trace.
 */
export function NoAccessPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const username = useAuthStore((s) => s.user?.username);
  const logout = useAuthStore((s) => s.logout);

  const signOut = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div
      data-testid="no-access"
      className="flex min-h-screen items-center justify-center bg-background p-6"
    >
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-muted-foreground" />
            <CardTitle>{t('noAccess.title')}</CardTitle>
          </div>
          <CardDescription>{t('noAccess.body')}</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-between gap-4">
          <span className="truncate text-sm text-muted-foreground">{username ?? '—'}</span>
          <Button variant="outline" onClick={() => void signOut()}>
            {t('auth.signOut')}
          </Button>
        </CardContent>
      </Card>
      <Watermark />
    </div>
  );
}
